import {
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Chip as MuiChip,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { SlaTargetIcon } from "components/icons/dual-tone/SlaTargetIcon";
import { SlaTimerIcon } from "components/icons/dual-tone/SlaTimerIcon";
import {
  type SlaTrackingData,
  type SlaTrackingStatus,
  statusData,
} from "shared";

const STATUS_LABEL: Record<SlaTrackingStatus, string> = {
  on_track: "On Track",
  met: "Met",
  at_risk: "At Risk",
  breached: "Breached",
  paused: "Paused",
  // pending: "Pending",
  pending: "Scheduled",
  disabled: "Disabled",
};

const getProgressColor = (
  status: SlaTrackingStatus | undefined,
): "success" | "warning" | "error" | "info" => {
  switch (status) {
    case "on_track":
    case "met":
    case "pending":
      return "success";
    case "at_risk":
      return "warning";
    case "breached":
      return "error";
    default:
      return "info";
  }
};

const formatRemaining = (minutes: number): string => {
  const isNegative = minutes < 0;
  const prefix = isNegative ? "-" : "";
  const abs = Math.ceil(Math.abs(minutes));
  if (abs === 0) return "0m";
  const days = Math.floor(abs / 1440);
  const hrs = Math.floor((abs % 1440) / 60);
  const mins = abs % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (parts.length === 0) parts.push("0m");
  return `${prefix}${parts.join(" ")}`;
};

type StatusColors = { stroke: string; fill: string };

const getStatusColorsFactory =
  (palette: Theme["palette"]) =>
  (status: SlaTrackingStatus | undefined): StatusColors => {
    const sys = palette.primitive.system;
    switch (status) {
      case "on_track":
        return { stroke: sys.green[800] ?? "", fill: sys.green[100] ?? "" };
      case "met":
        return {
          stroke: sys.blue[800] ?? "",
          fill: sys.blue[200] ?? "",
        };
      case "at_risk":
        return { stroke: sys.orange[800] ?? "", fill: sys.orange[100] ?? "" };
      case "breached":
        return { stroke: sys.red[800] ?? "", fill: sys.red[100] ?? "" };
      case "paused":
        return { stroke: sys.yellow[800] ?? "", fill: sys.yellow[100] ?? "" };
      case "pending":
        return { stroke: sys.violet[700] ?? "", fill: sys.violet[200] ?? "" };
      default:
        return { stroke: sys.green[800] ?? "", fill: sys.green[100] ?? "" };
    }
  };

const chipSx = (colors: StatusColors): SxProps<Theme> => ({
  color: colors.stroke,
  bgcolor: colors.fill,
  "&.MuiChip-root": {
    height: "20px",
    borderRadius: "4px",
    border: "none",
  },
  "& .MuiChip-label, & .MuiChip-labelSmall": {
    padding: "0 6px",
    fontSize: "10px !important",
    fontWeight: "500 !important",
  },
});

const getRemainingLabel = (
  remaining: number | null,
  status: SlaTrackingStatus | undefined,
  completedAt: string | null | undefined,
): string => {
  if (remaining == null) return "--";
  const abs = formatRemaining(Math.abs(remaining));
  if (completedAt) {
    return status === "breached"
      ? `${abs} after target`
      : `${abs} before target`;
  }
  if (status === "breached") return `Overdue by: ${abs}`;
  return `Remaining: ${abs}`;
};

interface SlaSectionProps {
  title: string;
  Icon: typeof SlaTimerIcon;
  status: SlaTrackingStatus | undefined;
  targetMinutes: number | null | undefined;
  elapsedMinutes: number | null | undefined;
  completedAt: string | null | undefined;
  colors: StatusColors;
  trackBgColor: string;
  isTicketClosed: boolean;
}

const SlaSection = ({
  title,
  Icon,
  status,
  targetMinutes,
  elapsedMinutes,
  completedAt,
  colors,
  trackBgColor,
  isTicketClosed,
}: SlaSectionProps) => {
  const remaining =
    targetMinutes != null && elapsedMinutes != null
      ? targetMinutes - elapsedMinutes
      : null;
  const progress =
    targetMinutes != null && targetMinutes > 0 && elapsedMinutes != null
      ? Math.min((elapsedMinutes / targetMinutes) * 100, 100)
      : 0;
  const theme = useTheme();

  return (
    <Stack gap="16px">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        marginLeft="-2px"
      >
        <Stack direction="row" alignItems="center" gap="8px">
          <Icon
            primaryColor={colors.stroke}
            secondaryColor={colors.fill}
            fontSize="small"
          />
          <Typography variant="caption-2">{title}</Typography>
        </Stack>
        <MuiChip
          variant="outlined"
          label={<Typography>{STATUS_LABEL[status ?? "on_track"]}</Typography>}
          size="small"
          sx={chipSx(colors)}
        />
      </Stack>
      <Stack gap="8px">
        {!isTicketClosed && (
          <LinearProgress
            variant="determinate"
            color={getProgressColor(status)}
            value={100 - progress}
            sx={{
              width: "340px",
              height: "6px",
              borderRadius: "4px",
              backgroundColor: trackBgColor,
              ...(status === "paused" && {
                "& .MuiLinearProgress-bar": {
                  backgroundColor: theme.palette.primitive.system.yellow[700],
                },
              }),
            }}
          />
        )}
        <Stack direction="row" justifyContent="space-between">
          <Typography
            variant="body-small"
            color={
              status === "breached"
                ? theme.palette.primitive.system.red[700]
                : theme.palette.lightBg.low
            }
          >
            {getRemainingLabel(remaining, status, completedAt)}
          </Typography>
          {isTicketClosed && status === "met" ? (
            <></>
          ) : (
            <Typography variant="body-small" color={theme.palette.lightBg.low}>
              {targetMinutes != null
                ? `Target: ${formatRemaining(targetMinutes)}`
                : "--"}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

interface SlaDetailsCardProps {
  trackingData: SlaTrackingData;
  /** Show only one section in the tooltip. Omit to show both. */
  showOnly?: "response" | "resolution";
  ticketStatus?: string;
}

function SlaDetailsCard({
  trackingData,
  showOnly,
  ticketStatus,
}: SlaDetailsCardProps) {
  const theme = useTheme();
  const getStatusColors = getStatusColorsFactory(theme.palette);

  const {
    firstResponseStatus,
    firstResponseTargetMinutes,
    firstResponseElapsedMinutes,
    firstResponseCompletedAt,
    resolutionStatus,
    resolutionTargetMinutes,
    resolutionElapsedMinutes,
    resolutionCompletedAt,
  } = trackingData;

  const isFirstResponseDisabled = firstResponseStatus === "disabled";
  const isResolutionDisabled = resolutionStatus === "disabled";

  const showResponse =
    !isFirstResponseDisabled && (!showOnly || showOnly === "response");
  const showResolution =
    !isResolutionDisabled && (!showOnly || showOnly === "resolution");
  const showDivider = showResponse && showResolution;
  const trackBgColor = theme.palette.neutral["020"] ?? "";

  return (
    <Card variant="outlined" sx={{ minWidth: 320, borderRadius: "8px" }}>
      <CardContent
        sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
      >
        {showResponse && (
          <SlaSection
            title="First response SLA"
            Icon={SlaTimerIcon}
            status={firstResponseStatus}
            targetMinutes={firstResponseTargetMinutes}
            elapsedMinutes={firstResponseElapsedMinutes}
            completedAt={firstResponseCompletedAt}
            colors={getStatusColors(firstResponseStatus)}
            trackBgColor={trackBgColor}
            isTicketClosed={ticketStatus === statusData.CLOSED}
          />
        )}
        {showDivider && <Divider />}
        {showResolution && (
          <SlaSection
            title="Resolution SLA"
            Icon={SlaTargetIcon}
            status={resolutionStatus}
            targetMinutes={resolutionTargetMinutes}
            elapsedMinutes={resolutionElapsedMinutes}
            completedAt={resolutionCompletedAt}
            colors={getStatusColors(resolutionStatus)}
            trackBgColor={trackBgColor}
            isTicketClosed={ticketStatus === statusData.CLOSED}
          />
        )}
      </CardContent>
    </Card>
  );
}

export default SlaDetailsCard;
